import {observable, action, computed} from 'mobx';
import {noop} from 'shared/tools';
import _ from 'lodash';
import {auth} from 'main/tools';
import SignatureViewModel from './SignatureViewModel';
import MERGE_FIELDS from 'main/stores/documents/mergeFields';
import {AssignmentRule} from 'main/stores/documents';
import MergeFieldViewModel from './MergeFieldViewModel';
import FormElementViewModel from './FormElementViewModel';
import {fromFabricToModel} from '../tools/converters';
import {
  createEmployeeSignature,
  createCompanySignature,
  createMergeField,
  createInputField
}
from '../tools/annotations';

class AnnotatablePdfPreviewState {
  fabric;
  store;
  pdfUrl;
  annotationModels = [];
  callbacks = {
    onAnnotationsAdded: noop,
    onPageRefreshed: noop,
    rerenderFabricCanvas: noop,
    onSave: noop,
    onCancel: noop
  };
  lastKnownBestCoords;

  @observable signerFields = {
    name: 'components.lexical.merge_fields.Signer Fields',
    options: [
      MergeFieldViewModel.fromHash({
        key: 'issued_date',
        name: 'components.lexical.merge_fields.Issued Date'
      }),
      MergeFieldViewModel.fromHash({
        key: 'signed_date',
        name: 'components.lexical.merge_fields.Signed Date'
      }),
      MergeFieldViewModel.fromHash({
        key: 'assignee_name',
        name: 'components.lexical.merge_fields.Signer Preferred Name'
      }),
      MergeFieldViewModel.fromHash({
        key: 'assignee_legal_name',
        name: 'components.lexical.merge_fields.Signer Name'
      })
    ]
  }

  @observable companyDocument;
  @observable annotations = [];
  @observable assignmentRules = [];
  @observable pageNumber = 1;
  @observable signatures = [];
  @observable mergeFields = [];
  @observable inputFields = [];
  @observable isLoaded = false;
  @observable canvasWidth;
  @observable canvasHeight;
  @observable selectedAssignee;
  @observable selectedAnnotation;

  constructor({companyDocument, pdfUrl, store, onSave = noop, onCancel = noop, annotations, fabric, assignmentRules}) {
    this.companyDocument = companyDocument;
    this.pdfUrl = pdfUrl;
    this.callbacks.onSave = onSave;
    this.callbacks.onCancel = onCancel;
    this.store = store;
    this.annotationModels = annotations || [];
    this.fabric = fabric;
    this.lastKnownBestCoords = {};
    this.assignmentRules = assignmentRules;
    this.selectedAssignee = this.selectedAssigneeOptions[0]
  }

  fromModelToDescriptor(annotation) {
    switch (annotation.annotationType) {
      case 'employee_signature':
        return SignatureViewModel.employeeSignature(
          annotation.annotationArgs.id,
          annotation.assignee,
          annotation.color
        );
      case 'company_signature':
        return SignatureViewModel.fromCompanySignature({
          id: annotation.annotationArgs.signatureId,
          name: annotation.state.signatureName,
          signatureData: annotation.state.signatureData
        });
      case 'custom_field':
      case 'variable':
        const key = annotation.annotationArgs.name === 'commitment' ? 'employment_type' : annotation.annotationArgs.name;
        const mergeField = _.find(_.flatMap([...this.mergeFields, this.signerFields], optionCategory => optionCategory.options.toJS()), {key});

        return _.merge(mergeField, {color: annotation.color, assignee: annotation.assignee})
      case 'input':
        return FormElementViewModel.fromHash({
          id: annotation.annotationArgs.id,
          inputType: annotation.annotationArgs.inputType,
          isRequired: annotation.annotationArgs.isRequired,
          isTotalClaimAmount: annotation.isTotalClaimAmount,
          isIncomeLessThanClaimAmount: annotation.isIncomeLessThanClaimAmount,
          isAdditionalTax: annotation.isAdditionalTax,
          assignee: annotation.assignee,
          color: annotation.color
        });
      default:
        throw new Error(`Annotation type ${annotation.annotationType} not supported.`);
    }
  }

  @action async load() {
    await this.store.load();

    const companySignatures = this.store.getCompanySignatures();
    this.signatures = companySignatures.map(SignatureViewModel.fromCompanySignature);

    this.mergeFields = _.cloneDeep(MERGE_FIELDS).map(category => {
      category.options = category.options.map(MergeFieldViewModel.fromHash);
      return category;
    });

    this.addCustomFieldsToMergeFields();

    this.inputFields = [
      {
        name: 'components.lexical.merge_fields.Forms',
        options: [
          SignatureViewModel.employeeSignature(),
          FormElementViewModel.textField(),
          FormElementViewModel.optionalTextField(),
          FormElementViewModel.textAreaField(),
          FormElementViewModel.optionalTextAreaField(),
          FormElementViewModel.checkboxField()
        ]
      },
      this.signerFields
    ];

    if (auth.company.id === '10265' || auth.company.id === '10401' || auth.company.id == '11161') {
      this.inputFields.push({
        name: 'components.lexical.merge_fields.Tax',
        options: [
          FormElementViewModel.totalClaimAmountField(),
          FormElementViewModel.incomeLessThanClaimAmountField(),
          FormElementViewModel.additionalTaxField()
        ]
      })
    }

    const descriptorsAndModels = this.annotationModels.map(
      model => (
        {
          descriptor: this.fromModelToDescriptor(model),
          model
        }
      )
    );

    await Promise.all(
      descriptorsAndModels.map(
        t => this.addAnnotation(t.descriptor, t.model)
      )
    );
  }

  addCustomFieldsToMergeFields() {
    const groups = this.store.getCustomFieldGroups();
    const customFieldMergeFields = _.chain(this.store.getCustomFields())
      .filter('employeeCanView')
      .sortBy('name')
      .groupBy('customFieldGroupId')
      .map((customFields, customFieldGroupId) => {
        return {
          name: _.find(groups, {id: customFieldGroupId}).name,
          alreadyTranslated: true,
          options: _.map(customFields, customField => MergeFieldViewModel.fromHash({
            name: customField.name, key: `custom_field_${customField.id}`, annotationType: 'custom_field', alreadyTranslated: true
          }))
        };
      })
      .value();
    this.mergeFields = this.mergeFields.concat(customFieldMergeFields);
  }

  @action async addAnnotation(descriptor, model = {}) {
    const annotation = await this.createAnnotation(descriptor, model);
    annotation.pageNumber = model.pageNumber || this.pageNumber;
    annotation.descriptor = descriptor;
    if (model.assignee !== 'employee') {
      annotation.assignee = model.assignee;
      annotation.color = model.color;
    }
    this.annotations.push(annotation);
    if (this.isLoaded) {
      this.callbacks.onAnnotationsAdded([annotation]);
    }
  }

  @action onLoaded() {
    this.isLoaded = true;
  }

  @action changePage(pageNumber, canvasWidth, canvasHeight) {
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.pageNumber = pageNumber;
    this.callbacks.onAnnotationsAdded(_.filter(this.annotations, {pageNumber: this.pageNumber}));
    this.callbacks.onPageRefreshed();
  }

  @action async save() {
    return this.callbacks.onSave(
      this.annotations.map(i => fromFabricToModel(i, this.canvasWidth, this.canvasHeight))
    );
  }

  @action cancel() {
    this.callbacks.onCancel();
  }

  async createAnnotation(descriptor, model) {
    switch (descriptor.annotationType) {
      case 'employee_signature':
        return createEmployeeSignature(descriptor, model, this.fabric);
      case 'company_signature':
        return createCompanySignature(descriptor, model, this.fabric);
      case 'variable':
      case 'custom_field':
        return createMergeField(descriptor, model, this.fabric);
      case 'input':
        return createInputField(descriptor, model, this.fabric);
      default:
        throw new Error(`Annotation type ${descriptor.annotationType} is not supported.`);
    }
  }

  @action onAnnotationDeleted(annotation) {
    this.annotations.remove(annotation);
    if (annotation.assignee && !this.hasAnnotation(annotation.assignee)) {
      this.companyDocument.removeRule(annotation.assignee);
    }
    this.selectedAnnotation = null;
  }

  @action onAssigneeChanged(assignee) {
    const assigneeId = assignee.assigneeType === 'manager' ? 'manager' : assignee.employee?.id;
    this.applyAssigneeToSelectedAnnotation(assignee, assigneeId);
    this.applyAssigneeColorToSelectedAnnotation(assigneeId);
  }

  @action applyAssigneeToSelectedAnnotation(assignee, assigneeId) {
    const previousAssigneeId = this.selectedAnnotation.assignee;
    if (assignee.assigneeType !== 'employee' && !this.hasAnnotation(assigneeId)) {
      this.companyDocument.addRule(assignee);
    }
    this.selectedAnnotation.assignee = assigneeId;
    if (previousAssigneeId && !this.hasAnnotation(previousAssigneeId)) {
      this.companyDocument.removeRule(previousAssigneeId);
    }
  }

  @action applyAssigneeColorToSelectedAnnotation(assigneeId) {
    const isVariable = this.selectedAnnotation.descriptor.annotationType === 'variable';
    const defaultColor = isVariable ? '#000000' : '#c4cdd5';
    this.selectedAnnotation.color = assigneeId ? this.assignmentRules.find((rule) => rule.assignee === assigneeId).color : defaultColor;

    if (this.selectedAnnotation.descriptor.annotationType === 'variable') {
      this.selectedAnnotation.set('fill', this.selectedAnnotation.color);
    } else if (this.selectedAnnotation.type === 'group') {
      _.find(this.selectedAnnotation.getObjects(), {type: 'rect'}).set('stroke', this.selectedAnnotation.color)
    } else {
      this.selectedAnnotation.set('stroke', this.selectedAnnotation.color)
    }

    this.callbacks.rerenderFabricCanvas();
  }

  hasAnnotation(assigneeId) {
    return _.some(this.annotations, {assignee: assigneeId});
  }

  @computed get customFieldMergeFields() {
    return this.mergeFields.filter(field => field.annotationType === 'custom_field');
  }

  @computed get selectedAssigneeOptions() {
    return [
      new AssignmentRule({assigneeType: 'employee'}),
      ...this.sortedAssignmentRules
    ];
  }

  @computed get sortedAssignmentRules() {
    return _.sortBy(this.assignmentRules, 'order');
  }


  @computed get assigneeSelectorMenuPosition() {
    const position = {};

    if (!this.selectedAnnotation?.canvas) return position;

    position.top = this.selectedAnnotation.top
    const left = (this.selectedAnnotation.left + (this.selectedAnnotation.width * this.selectedAnnotation.scaleX)) + 20;
    if (left <= this.selectedAnnotation.canvas.width - 100) {
      position.left = left;
    } else {
      position.right = this.selectedAnnotation.canvas.width - this.selectedAnnotation.left + 20;
    }

    return position;
  }

  registerCallback(event, callback) {
    switch (event) {
      case 'onAnnotationsAdded':
      case 'onDeleteKeyPressed':
      case 'rerenderFabricCanvas':
      case 'onPageRefreshed':
        this.callbacks[event] = callback;
        break;
      default:
        throw new Error(`Callback ${event} is not supported.`);
    }
  }
}

export default AnnotatablePdfPreviewState;
